import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Info, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { SaveSearchButton } from "@/components/SaveSearchButton";
import { RecipeChips } from "@/components/finder/RecipeChips";
import { MatchCard } from "@/components/finder/MatchCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { SPORTS } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

const NARROW = [["conference", "conferences", "All conferences"], ["team", "teams", "All teams"], ["class_year", "class_years", "All classes"]];
const parse = (s) => { try { return s ? JSON.parse(s) : null; } catch { return null; } };

const SportChips = ({ sports, value, onPick, testPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {sports.map((k) => (
      <button key={k} data-testid={`${testPrefix}-${k}`} onClick={() => onPick(k)}
        className={`rounded-full border px-3.5 py-1.5 font-data text-[11px] uppercase tracking-wider transition-colors duration-200 ${value === k ? "border-[#FF2D78] bg-[#FF2D78] text-white" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
        {SPORTS[k]?.label || k}
      </button>
    ))}
  </div>
);

const Narrow = ({ result, narrow, setNarrow }) => (
  <div className="flex flex-wrap gap-2">
    {NARROW.map(([k, list, label]) => (
      <Select key={k} value={narrow[k] || "all"} onValueChange={(v) => setNarrow((n) => ({ ...n, [k]: v === "all" ? null : v }))}>
        <SelectTrigger data-testid={`finder-narrow-${k}`} className="h-9 w-44 border-slate-800 bg-[#131927] text-xs"><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-80 border-slate-800 bg-[#131927]">
          <SelectItem value="all">{label}</SelectItem>
          {(result?.[list] || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    ))}
  </div>
);

export default function FinderPage() {
  const [sp, setSp] = useSearchParams();
  const [catalog, setCatalog] = useState(null);
  const [sport, setSport] = useState(() => parse(sp.get("recipe"))?.sport || "volleyball");
  const [protos, setProtos] = useState([]);
  const [text, setText] = useState("");
  const [recipe, setRecipe] = useState(() => parse(sp.get("recipe")));
  const [ambiguous, setAmbiguous] = useState(null);
  const [thinking, setThinking] = useState(false);
  const [narrow, setNarrow] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/prototypes/catalog").then((r) => setCatalog(r.data.sports)); }, []);
  useEffect(() => {
    const like = sp.get("like");
    if (like) api.get(`/prototypes/like/${like}`).then((r) => { setSport(r.data.recipe.sport); setRecipe(r.data.recipe); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/prototypes", { params: { sport } }).then((r) => setProtos(r.data.items)); }, [sport]);

  const run = useCallback(async (skip = 0) => {
    if (!recipe) return;
    setLoading(true);
    try {
      const r = (await api.post("/prototypes/match", { recipe, ...narrow, skip })).data;
      setResult((prev) => (skip ? { ...r, items: [...prev.items, ...r.items] } : r));
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [recipe, narrow]);
  useEffect(() => { run(0); }, [run]);
  useEffect(() => { if (recipe) setSp({ recipe: JSON.stringify(recipe) }, { replace: true }); }, [recipe, setSp]);

  const pick = (r) => { setAmbiguous(null); setNarrow({}); setSport(r.sport); setRecipe(r); };
  const interpret = async (forced) => {
    if (text.trim().length < 2) return;
    setThinking(true);
    try {
      const r = (await api.post("/prototypes/interpret", { text: text.trim(), sport: forced || null })).data;
      if (r.recipe) pick(r.recipe);
      else setAmbiguous(r.ambiguous_sports);
    } catch (e) { toast.error(formatApiError(e)); }
    setThinking(false);
  };

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      <AppHeader />
      <main className="mx-auto max-w-[1400px] px-4 pb-24 pt-10 sm:px-8">
        <div className="rise mb-8">
          <p className="eyebrow !text-[#FF2D78]">Prototype finder</p>
          <h1 className="mt-3 font-display text-4xl font-black uppercase tracking-tight sm:text-5xl lg:text-6xl">Find me a player like this.</h1>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); interpret(); }} className="flex gap-3">
          <div className="relative flex-1">
            <Sparkles className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#FF2D78]" />
            <input data-testid="finder-text-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={300}
              placeholder="Describe the player you're looking for…"
              className="h-14 w-full rounded-xl border border-slate-800 bg-[#131927] pl-14 pr-4 text-base text-white outline-none transition-colors duration-200 placeholder:text-slate-500 focus:border-[#FF2D78]" />
          </div>
          <button data-testid="finder-submit-button" disabled={thinking || text.trim().length < 2}
            className="inline-flex h-14 items-center gap-2 rounded-xl bg-[#FF2D78] px-6 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#E01F66] disabled:opacity-50">
            {thinking ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Find
          </button>
        </form>
        {ambiguous && (
          <div data-testid="finder-ambiguous" className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
            <p className="mb-3 text-sm text-amber-200">Which sport did you mean?</p>
            <SportChips sports={ambiguous} onPick={(s) => { setSport(s); interpret(s); }} testPrefix="finder-ambiguous-sport" />
          </div>
        )}
        <div className="mt-8"><SportChips sports={Object.keys(SPORTS)} value={sport} onPick={(s) => { setSport(s); setAmbiguous(null); if (recipe?.sport !== s) { setRecipe(null); setResult(null); setSp({}, { replace: true }); } }} testPrefix="finder-sport" /></div>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {protos.map((p) => (
            <button key={p.slug} data-testid={`prototype-tile-${p.slug}`} onClick={() => pick({ ...p.recipe, sport: p.sport, label: p.name })}
              className={`group rounded-xl border bg-[#131927] p-4 text-left transition-[transform,border-color] duration-200 hover:-translate-y-0.5 ${recipe?.label === p.name && recipe?.sport === p.sport ? "border-[#FF2D78]" : "border-slate-800 hover:border-slate-600"}`}>
              <div className="h-1 w-8 rounded-full" style={{ background: SPORTS[p.sport]?.accent }} />
              <div className="mt-3 font-display text-lg font-extrabold uppercase leading-tight text-white">{p.name}</div>
              <div className="mt-1 text-xs text-slate-400">{p.description}</div>
            </button>
          ))}
        </div>
        {recipe && (
          <section className="mt-10 space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="eyebrow mb-3">{recipe.like_id ? `${recipe.label} · what we're matching on` : "What we're matching on"}</p>
                <RecipeChips recipe={recipe} catalog={catalog} onChange={setRecipe} />
              </div>
              <SaveSearchButton params={{ sport: recipe.sport, recipe: JSON.stringify(recipe) }} suggestedName={`${recipe.label || "Prototype"} · ${SPORTS[recipe.sport]?.label}`} />
            </div>
            {result?.gaps?.map((g) => (
              <p key={g} data-testid="finder-gap-note" className="flex items-start gap-2 rounded-lg border border-slate-800 bg-[#131927]/60 px-4 py-3 text-sm text-slate-300">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#FF2D78]" />{g}
              </p>
            ))}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p data-testid="finder-results-count" className="eyebrow">{result ? `${result.total} player${result.total === 1 ? "" : "s"} · ${result.strong} strong match${result.strong === 1 ? "" : "es"} (70+)` : "Matching"}</p>
              <Narrow result={result} narrow={narrow} setNarrow={setNarrow} />
            </div>
            <div className="grid grid-cols-1 gap-5 min-[480px]:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {!result && [...Array(4)].map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-xl bg-[#131927]" />)}
              {result?.items.map((m, i) => <MatchCard key={m.player.id} m={m} index={i} />)}
            </div>
            {result && result.items.length < result.total && (
              <div className="flex justify-center">
                <button data-testid="finder-load-more" onClick={() => run(result.items.length)} disabled={loading}
                  className="rounded-full border border-slate-700 px-8 py-3 font-data text-xs uppercase tracking-widest text-slate-200 transition-colors duration-200 hover:border-[#FF2D78] disabled:opacity-50">
                  {loading ? "Loading" : `Load more (${result.total - result.items.length} left)`}
                </button>
              </div>
            )}
            {result && !result.total && <p data-testid="finder-no-results" className="rounded-xl border border-dashed border-slate-800 p-12 text-center text-sm text-slate-400">No players match. Remove a chip to widen the search.</p>}
          </section>
        )}
      </main>
    </div>
  );
}
